import { useMemo, useState } from 'react';
import {
  ChakraFlex,
  DatePickerInput,
  EquipUIFireflyV2Theme,
  FAIconName,
  FireflyButton,
  InputGroupV2,
  ModalV2,
  ProviderAvatarBlock,
  Tag,
  TagColor,
  Text,
} from '@equip.health/ui';
import { PatientEvents } from '~/analytics/patientEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import { useUpdateReassignmentDateMutation } from '~/store/services/clinicalData';
import { DateTime } from 'luxon';

const TOP_LEVEL_ID = 'edit-reassignment-date-modal';

interface ReassignmentProviderInfo {
  firstName: string;
  lastName: string;
  displayFirstName?: string;
  displayLastName?: string;
  credentials?: string[];
  avatarSrc?: string;
  pronouns?: string[];
  role: string;
}

interface EditReassignmentDateModalProps {
  reassignmentId: string;
  currentDate: string;
  patientId: string;
  onClose: () => void;
  onSaveSuccess: () => void;
  departingProvider: ReassignmentProviderInfo;
  incomingProvider: ReassignmentProviderInfo;
}

const ReassignmentProviderCard = ({
  provider,
  tagColor,
  tagLabel,
  idPrefix,
}: {
  provider: ReassignmentProviderInfo;
  tagColor: TagColor;
  tagLabel: string;
  idPrefix: string;
}) => (
  <ChakraFlex
    alignItems="center"
    backgroundColor={EquipUIFireflyV2Theme.colors.neutral.background.secondary}
    borderRadius="12px"
    justifyContent="space-between"
    padding="16px"
  >
    <ProviderAvatarBlock
      avatarUrl={provider.avatarSrc}
      credentials={provider.credentials}
      displayName={
        provider.displayFirstName && provider.displayLastName
          ? {
              firstName: provider.displayFirstName,
              lastName: provider.displayLastName,
            }
          : undefined
      }
      id={`${TOP_LEVEL_ID}__${idPrefix}-provider`}
      legalName={{
        firstName: provider.firstName,
        lastName: provider.lastName,
      }}
      pronouns={provider.pronouns}
      providerTypeName={provider.role}
      shouldShowCredentials
      shouldShowPronouns
      shouldShowProviderType
      variant="standard"
    />
    <Tag
      color={tagColor}
      id={`${TOP_LEVEL_ID}__${idPrefix}-tag`}
      size="sm"
      value={tagLabel}
    />
  </ChakraFlex>
);

const EditReassignmentDateModal = ({
  reassignmentId,
  currentDate,
  patientId,
  onClose,
  onSaveSuccess,
  departingProvider,
  incomingProvider,
}: EditReassignmentDateModalProps) => {
  const { trackEvent } = useAnalytics();
  const { showErrorToast } = useErrorToast();
  const [updateReassignmentDate, { isLoading }] =
    useUpdateReassignmentDateMutation();

  const [selectedDate, setSelectedDate] = useState(currentDate);

  const tomorrow = useMemo(
    () => DateTime.now().plus({ days: 1 }).startOf('day'),
    [],
  );
  const minDate = useMemo(() => tomorrow.toJSDate(), [tomorrow]);
  const defaultDate = useMemo(
    () => DateTime.fromISO(currentDate).toJSDate(),
    [currentDate],
  );
  const hasChanged = selectedDate !== currentDate;

  const handleSubmit = async () => {
    try {
      await updateReassignmentDate({
        reassignmentId,
        reassignmentDate: selectedDate,
      }).unwrap();
      onSaveSuccess();
      trackEvent(PatientEvents.reassignmentDateEdit, {
        patientId,
        source: AnalyticsSourceValues.CARE_PLAN_TAB,
      });
      onClose();
    } catch (error) {
      showErrorToast(
        GlobalErrorCode.UPDATE_CARE_TEAM,
        error.status,
        error.data?.message,
      );
    }
  };

  return (
    <ModalV2
      isOpen
      onClose={onClose}
      title="Edit reassignment date"
    >
      <Text marginBottom="16px" variant="caption">
        The reassigning providers start and end on the same date.
      </Text>
      <ChakraFlex direction="column" gap="24px" marginBottom="24px">
        <ReassignmentProviderCard
          idPrefix="departing"
          provider={departingProvider}
          tagColor={TagColor.red}
          tagLabel="Ending"
        />
        <ReassignmentProviderCard
          idPrefix="incoming"
          provider={incomingProvider}
          tagColor={TagColor.teal}
          tagLabel="Starting"
        />
      </ChakraFlex>
      <InputGroupV2
        id={`${TOP_LEVEL_ID}-date-group`}
        isRequired
        label="Reassignment date"
      >
        <DatePickerInput
          defaultDate={defaultDate}
          id={`${TOP_LEVEL_ID}-date-picker`}
          minDate={minDate}
          onSubmit={(date: Date) => {
            setSelectedDate(DateTime.fromJSDate(date).toISODate());
          }}
        />
      </InputGroupV2>
      <ChakraFlex
        alignItems="center"
        justifyContent="space-between"
        marginTop="24px"
        width="100%"
      >
        <FireflyButton
          id={`${TOP_LEVEL_ID}-cancel-button`}
          leftIcon={FAIconName.timesRegular}
          onClick={onClose}
          variant="secondary"
        >
          Cancel
        </FireflyButton>
        <FireflyButton
          id={`${TOP_LEVEL_ID}-save-button`}
          isDisabled={!hasChanged}
          isLoading={isLoading}
          leftIcon={FAIconName.checkRegular}
          onClick={handleSubmit}
          variant="primary"
        >
          Save
        </FireflyButton>
      </ChakraFlex>
    </ModalV2>
  );
};

export default EditReassignmentDateModal;
